import React, { useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import type { SidebarItem } from "./DashboardLayout";

interface DashboardOutletContext {
  sidebarItems: SidebarItem[];
}

const DashboardHome: React.FC = () => {
  const navigate = useNavigate();
  const { sidebarItems } = useOutletContext<DashboardOutletContext>();

  const navigationItems = useMemo(() => {
    const byPath = new Map<string, SidebarItem>();
    sidebarItems.forEach((item) => {
      if (!byPath.has(item.path)) {
        byPath.set(item.path, item);
      }
    });
    return Array.from(byPath.values());
  }, [sidebarItems]);

  if (!navigationItems.length) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
        No modules are available for your role yet. Please contact your
        administrator if you believe this is incorrect.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">
          Welcome to your workspace
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          Everything you can access is listed below. Pick a module to jump
          straight into it.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              type="button"
              onClick={() => navigate(item.path)}
              className="group flex h-full w-full flex-col rounded-lg border border-gray-200 bg-white p-5 text-left shadow-sm transition hover:border-primary-200 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-base font-semibold text-gray-900">
                      {item.name}
                    </span>
                    {item.badge && (
                      <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-600">
                        {item.badge}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-gray-600">
                    {item.description ||
                      "Open this section to view detailed tools and actions."}
                  </p>
                </div>
              </div>
              <span className="mt-4 text-sm font-medium text-primary-600 transition group-hover:text-primary-700">
                Open {item.name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DashboardHome;
